/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/_chrome/left-rail.tsx`.
 *
 * No next/* imports in the origin; copied into client/shell/chrome/ so the
 * Vite shell never imports from app/ (PRD D6).
 */

import { type ReactNode } from "react";
import { useRailExtension } from "@excellent/ui";
import { SectionRail } from "./section-rail";

/**
 * The left navigation column — now context-gated. The main app (project /
 * department / core surfaces) navigates entirely from the top nav (the
 * Projects, Department, and Tools dropdowns), so the rail no longer renders
 * there. It is kept ONLY where it is the sole navigation:
 *  - any surface that pushes a LEFT rail extension (the docs viewer's sibling
 *    list + on-this-page TOC).
 *
 * Settings and Database both moved their nav to the right-gutter `ContextRail`
 * (a `side:"right"` extension pushed by `app/settings/_rail.tsx` /
 * `app/database/_rail.tsx`), so neither lights this left column — their bodies
 * go full-width like analytics. See SETTINGS-REDESIGN.md.
 *
 * When nothing pushes a left extension the whole column — padding, workspace
 * switcher and all — is removed so the content area takes the full width. This
 * is a client component because the extension signal (`useRailExtension`) is
 * client state; gating here (rather than in the server layout) lets the empty
 * column disappear entirely instead of leaving an empty husk.
 */
export function LeftRail({
  topSlot,
}: {
  topSlot?: ReactNode;
}) {
  // A right-side extension floats in the right gutter well (ContextRail), so it
  // must NOT light up this left column — else the doc nav would show on both
  // sides (the duplication the docs viewer hit before).
  const ext = useRailExtension();
  const hasExtension = ext != null && ext.side !== "right";

  if (!hasExtension) return null;

  // Deliberately flat — no card, border, or shadow. The rail is navigation
  // chrome, not a surface: it sits directly on the page substrate so the
  // content area reads as the only surface.
  //
  // The primary ModeRail floats over the left margin (absolute, out of flow).
  // The ROW now reserves the rail's width as symmetric padding (`md:px-36` in
  // layout.tsx), so this in-flow column already starts clear of the rail — no
  // local `md:ml-36` needed (that double-inset it). The matching right reserve
  // re-centers the content well on the full viewport.
  return (
    // `data-secondary-rail`: this in-flow nav column takes real layout width, so
    // the page column must NOT bleed out under the floating rails (it would
    // overlap this one). The flag zeroes `--gut` on the row — see
    // `.page-scroll-row` in globals.css.
    <div className="hidden md:flex" data-secondary-rail>
      <SectionRail topSlot={topSlot} />
    </div>
  );
}
